export const PORTFOLIO_OPTIMIZATION_EVIDENCE_SCHEMA_VERSION = 1;
export const PORTFOLIO_OPTIMIZATION_EVIDENCE_TYPE = "MODELED_DECISION_PORTFOLIO";
export const PORTFOLIO_OPTIMIZATION_SOURCE_AUTHORITY = "PORTFOLIO_CAPITAL_OPTIMIZATION";
export const PORTFOLIO_OPTIMIZATION_SOURCE_ROUTE = "/investment-optimization";

const hasNumber = (value) => typeof value === "number" && Number.isFinite(value);
const hasNumbers = (object, keys) => object && keys.every((key) => hasNumber(object[key]));

export function isPortfolioCapitalOptimizationResponse(result) {
  return Boolean(
    result && typeof result === "object" && typeof result.scope === "string" &&
    hasNumbers(result, ["available_capital", "modeled_optimal_deployment", "unallocated_capital", "capital_utilization_ratio"]) &&
    hasNumbers(result.baseline, ["expected_annual_loss", "p90", "p95"]) &&
    hasNumbers(result.optimized, ["expected_annual_loss", "p90", "p95"]) &&
    hasNumbers(result.impact, ["eal_reduction", "modeled_economic_benefit", "p90_reduction", "p95_reduction", "net_benefit", "roi"]) &&
    Array.isArray(result.allocation) && result.methodology && typeof result.methodology === "object" &&
    result.run_context && typeof result.run_context === "object"
  );
}

export function createPortfolioOptimizationEvidence(result, acceptedAt = new Date().toISOString()) {
  if (!isPortfolioCapitalOptimizationResponse(result)) throw new TypeError("Invalid Portfolio Capital Optimization response");
  return {
    schemaVersion: PORTFOLIO_OPTIMIZATION_EVIDENCE_SCHEMA_VERSION,
    evidenceType: PORTFOLIO_OPTIMIZATION_EVIDENCE_TYPE,
    sourceAuthority: PORTFOLIO_OPTIMIZATION_SOURCE_AUTHORITY,
    acceptanceStatus: "ACCEPTED",
    acceptedAt,
    sourceRoute: PORTFOLIO_OPTIMIZATION_SOURCE_ROUTE,
    optimizerResponse: result,
  };
}

export function isPortfolioOptimizationEvidence(value) {
  return Boolean(value && value.schemaVersion === PORTFOLIO_OPTIMIZATION_EVIDENCE_SCHEMA_VERSION &&
    value.evidenceType === PORTFOLIO_OPTIMIZATION_EVIDENCE_TYPE &&
    value.sourceAuthority === PORTFOLIO_OPTIMIZATION_SOURCE_AUTHORITY && value.acceptanceStatus === "ACCEPTED" &&
    typeof value.acceptedAt === "string" && value.sourceRoute === PORTFOLIO_OPTIMIZATION_SOURCE_ROUTE &&
    isPortfolioCapitalOptimizationResponse(value.optimizerResponse));
}

const sortedIds = (ids) => Array.isArray(ids) ? ids.map(String).sort() : null;
const same = (left, right) => JSON.stringify(left) === JSON.stringify(right);
const normalized = (value) => typeof value === "string" ? value.toUpperCase() : value;

export function isEvidenceCurrent(evidence, currentContext) {
  if (!isPortfolioOptimizationEvidence(evidence) || !currentContext) return false;
  const run = evidence.optimizerResponse.run_context;
  const comparisons = [
    [sortedIds(run.scenario_ids), sortedIds(currentContext.scenarioIds)],
    [normalized(run.currency), normalized(currentContext.currency)],
    [run.simulations, currentContext.simulations],
    [run.seed, currentContext.seed],
    [normalized(run.seed_mode), normalized(currentContext.seedMode)],
    [run.economic_horizon_years, currentContext.economicHorizonYears],
  ];
  return comparisons.every(([accepted, current]) => accepted == null || current == null || same(accepted, current));
}
